// §4.14 THE FORECAST, /sales/forecast (Module 09, P3-E2 PR E2-1; §3.11,
// D-312 ruling 10).
//
// THE FORECAST IS SERVED, NEVER COMPUTED HERE. `GET /v1/opportunities/forecast`
// answers per close month and currency: the open deals, their expected value,
// their weighted value (each deal's `sales_weighted_minor`, rounded half-even
// once per deal and summed by the server), the deals without a value, and
// what was won in the month. This screen places those rows in the range and
// formats them. It adds no two figures, so a cell reads exactly what the list
// filtered to that month adds up to (§3.11). The one division on the page is
// the Weighted bar's width, a picture beside the number the table states.
//
// ONE TABLE PER CURRENCY. No cell adds two currencies (§9.13): the slipped row
// first (open deals whose close month has passed), then every month of the
// range, an empty month reading zero deals. Under each table, when non-zero,
// one line counts the won deals whose order was cancelled; they add no value.
//
// A VALUE DOOR (§7 narrowing 7). A reader the role mirror knows is refused
// `sales.read_value` reads one sentence and the page sends no request; a
// reader the mirror cannot judge (a tenant's composed role) is asked, and a
// 403 naming `sales.read_value` reads the same sentence. A lineage the
// pipeline refuses reads the area's refusal.
//
// A WON DEAL WITHOUT A VALUE IS COUNTED APART (§3.11): the Won cell names how
// many of the month's wins carry no value, beside the served won value, so a
// month whose only win had none never reads as nothing won.
//
// THE FILTERS BELONG TO A READER THE FORECAST ANSWERS. A reader the value
// door refuses reads one sentence, and an owner or customer picker above it
// would filter nothing, so the pickers render only beside a forecast. A
// filter that leaves the range empty reads the filtered-empty state with
// Clear filters, which keeps the range.
//
// EACH OPEN CELL LINKS TO THE LIST FILTERED TO ITS MONTH, under the
// forecast's own owner and customer filters (`forecastMonthListSearch`), so
// the list adds up to a Deals, Pipeline or Weighted cell. The list has no
// value filter, so the Without a value cell opens the same month, where its
// deals are the rows that show no value. The slipped row links to the open
// deals that closed before this month. The Won cell is not a link: it counts
// deals by the month they closed, which is not a filter the list has.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatMinor } from "../lib/money";
import { isAssignedOnlyReader } from "../lib/exports";
import {
  addMonths,
  definedOnly,
  forecastTables,
  formatMonth,
  monthRange,
  type ForecastTable,
} from "../lib/pipeline";
import { isSalesValueRefusal, valueSortVerdict } from "../lib/value-order";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useReadsHeldOffline, useRevealLatch } from "../lib/reads";
import { useOpportunityForecast } from "../hooks/opportunity-queries";
import { PipelineFilters, PipelineTabs } from "../components/pipeline/pipeline-parts";
import { compactSearch } from "./search-params";
import {
  FORECAST_MAX_SPAN_MONTHS,
  forecastMonthListSearch,
  validateSalesForecastSearch,
  type SalesForecastSearch,
} from "./pipeline-search-params";
import { bookClientPager } from "../hooks/book-pages";

/** One line of a forecast table: the slipped row, or a month. */
export interface ForecastLine {
  readonly key: string;
  readonly month: string | undefined;
  readonly openCount: number;
  readonly unvaluedCount: number;
  readonly openValue: number;
  readonly weightedValue: number;
  readonly wonCount: number | undefined;
  readonly wonValue: number | undefined;
  readonly wonUnvalued: number | undefined;
}

/** A table's lines, slipped first (pure). An absent month reads as zero deals
 * in it, which is what the server's sparse rows mean. */
export function forecastLines(table: ForecastTable): ForecastLine[] {
  const lines: ForecastLine[] = [];
  if (table.slipped !== undefined) {
    lines.push({
      key: "slipped",
      month: undefined,
      openCount: table.slipped.open_count,
      unvaluedCount: table.slipped.unvalued_count,
      openValue: table.slipped.open_value_minor,
      weightedValue: table.slipped.weighted_value_minor,
      wonCount: undefined,
      wonValue: undefined,
      wonUnvalued: undefined,
    });
  }
  for (const { month, row } of table.months) {
    lines.push({
      key: month,
      month,
      openCount: row?.open_count ?? 0,
      unvaluedCount: row?.unvalued_count ?? 0,
      openValue: row?.open_value_minor ?? 0,
      weightedValue: row?.weighted_value_minor ?? 0,
      wonCount: row?.won_count ?? 0,
      wonValue: row?.won_value_minor ?? 0,
      wonUnvalued: row?.won_unvalued_count ?? 0,
    });
  }
  return lines;
}

export function SalesForecastScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();

  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = useMemo(() => validateSalesForecastSearch(rawSearch), [rawSearch]);
  const setSearch = (next: SalesForecastSearch) => {
    void navigate({ to: "/sales/forecast", search: compactSearch(next), replace: true });
  };

  const refused = isSalesReadRefused(perms, perms.canOpenPipeline);
  const verdict = valueSortVerdict(perms);
  const valueKnownRefused = verdict === "refused";
  const owners = useOwnerIndex(perms.isAdmin);
  const offline = useReadsHeldOffline();
  // An admin's owner filter lists the member directory: the filters and the
  // tables wait for it, so the row never widens after it paints (§5). A
  // directory read the browser holds offline reports pending until the
  // network returns, so it counts as settled while offline, as on the board
  // and the list, and the owner filter offers no names until it answers.
  // The reveal latches, so the directory pending again when the network
  // returns never takes the painted tables back to the skeleton.
  const ownersSettled = useRevealLatch(!perms.isAdmin || !owners.isPending || offline);
  const [rangeInvalid, setRangeInvalid] = useState(false);

  const query = useMemo(
    () =>
      definedOnly({
        from_month: search.from_month,
        to_month: search.to_month,
        owner_user_id: search.owner,
        customer_id: search.customer,
      }),
    [search],
  );
  const forecast = useOpportunityForecast(
    query,
    !refused && !valueKnownRefused && verdict !== "pending",
  );

  useEffect(() => {
    if (forecast.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [forecast.error, navigate]);

  const tenantMonth = forecast.data?.tenant_month;
  const from = search.from_month ?? tenantMonth;
  const to = search.to_month ?? (from === undefined ? undefined : addMonths(from, 5));
  const months = useMemo(
    () => (from !== undefined && to !== undefined ? monthRange(from, to) : []),
    [from, to],
  );
  const tables = useMemo(
    () =>
      forecast.data === undefined
        ? []
        : forecastTables(forecast.data.rows, forecast.data.slipped, months),
    [forecast.data, months],
  );

  const valueRefused = valueKnownRefused || isSalesValueRefusal(forecast.error);
  const lineageRefused =
    forecast.error instanceof RequestError && forecast.error.httpStatus === 403 && !valueRefused;
  const shared = { owner: search.owner, customer: search.customer };
  const isFiltered = search.owner !== undefined || search.customer !== undefined;

  const writeRange = (nextFrom: string | undefined, nextTo: string | undefined) => {
    if (nextFrom !== undefined && nextTo !== undefined) {
      const span = monthRange(nextFrom, nextTo).length - 1;
      if (nextTo < nextFrom || span > FORECAST_MAX_SPAN_MONTHS) {
        setRangeInvalid(true);
        return;
      }
    }
    setRangeInvalid(false);
    setSearch({ ...search, from_month: nextFrom, to_month: nextTo });
  };

  const body = (() => {
    if (refused || lineageRefused) {
      return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pipeline")} />;
    }
    if (valueRefused) {
      return (
        <p className="sales-hint" role="status" data-testid="sales-forecast-no-value">
          {t("sales.pipeline.forecast.noValue")}
        </p>
      );
    }
    if (forecast.data === undefined || !ownersSettled) {
      if (forecast.data === undefined && forecast.fetchStatus === "paused") {
        return <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />;
      }
      if (
        forecast.data === undefined &&
        forecast.error !== null &&
        !(forecast.error instanceof UnauthorizedError)
      ) {
        const badRange =
          forecast.error instanceof RequestError && forecast.error.httpStatus === 400;
        return (
          <ErrorState
            title={t("sales.pipeline.forecast.error")}
            detail={badRange ? t("sales.pipeline.forecast.rangeInvalid") : undefined}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(forecast.error)}
            onRetry={() => {
              void forecast.refetch();
            }}
          />
        );
      }
      return (
        <div data-testid="sales-forecast-loading">
          <TableSkeleton rows={7} cols={6} label={t("sales.pipeline.forecast.loading")} />
        </div>
      );
    }
    if (tables.length === 0 && isFiltered) {
      return (
        <div data-testid="sales-forecast-filtered-empty">
          <EmptyState
            title={t("sales.pipeline.forecast.filteredEmptyTitle")}
            body={t("sales.pipeline.forecast.filteredEmptyBody")}
            ctaLabel={t("sales.pipeline.clearFilters")}
            onCta={() => {
              setSearch({ ...search, owner: undefined, customer: undefined });
            }}
          />
        </div>
      );
    }
    if (tables.length === 0) {
      return (
        <div data-testid="sales-forecast-empty">
          <EmptyState
            title={t("sales.pipeline.forecast.emptyTitle")}
            body={t("sales.pipeline.forecast.emptyBody")}
          />
        </div>
      );
    }
    return tables.map((table) => (
      <ForecastCurrencyTable
        key={table.currency}
        table={table}
        filters={shared}
        tenantMonth={tenantMonth}
        language={i18n.language}
      />
    ));
  })();

  return (
    <section className="sales-forecast-screen" data-testid="sales-forecast-screen">
      <PageHeader title={t("sales.pipeline.forecast.title")} />

      <PipelineTabs active="forecast" shared={shared} />

      {refused || valueRefused || !ownersSettled ? null : (
        <>
          <PipelineFilters
            owner={search.owner}
            customer={search.customer}
            onOwner={(owner) => {
              setSearch({ ...search, owner });
            }}
            onCustomer={(customer) => {
              setSearch({ ...search, customer });
            }}
            showOwner={perms.isAdmin}
            owners={owners.byId}
            ownersPending={owners.isPending && !offline}
            testPrefix="sales-forecast"
          />
          <div className="filter-bar" data-testid="sales-forecast-range">
            <Field label={t("sales.pipeline.forecast.rangeFrom")}>
              <input
                type="month"
                value={from ?? ""}
                data-testid="sales-forecast-from"
                onChange={(event) => {
                  const value = event.target.value;
                  writeRange(value.length > 0 ? value : undefined, search.to_month);
                }}
              />
            </Field>
            <Field label={t("sales.pipeline.forecast.rangeTo")}>
              <input
                type="month"
                value={to ?? ""}
                data-testid="sales-forecast-to"
                onChange={(event) => {
                  const value = event.target.value;
                  writeRange(search.from_month ?? from, value.length > 0 ? value : undefined);
                }}
              />
            </Field>
          </div>
          {rangeInvalid ? (
            <p className="sales-error-note" role="alert" data-testid="sales-forecast-range-invalid">
              {t("sales.pipeline.forecast.rangeInvalid")}
            </p>
          ) : null}
          {isAssignedOnlyReader(perms) ? (
            <p className="sales-hint" data-testid="sales-forecast-scope">
              {t("sales.pipeline.forecast.scopeSales")}
            </p>
          ) : null}
        </>
      )}

      <div className="sales-forecast-body" data-testid="sales-forecast-body">
        {body}
      </div>
    </section>
  );
}

function CellLink({
  month,
  filters,
  children,
  testId,
  language,
}: {
  readonly month: string;
  readonly filters: Pick<SalesForecastSearch, "owner" | "customer">;
  readonly children: ReactNode;
  readonly testId: string;
  readonly language: string;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to="/sales/opportunities"
      search={forecastMonthListSearch(month, filters)}
      className="ds-lnk num"
      data-testid={testId}
      data-month={month}
      aria-label={t("sales.pipeline.forecast.cellLink", { month: formatMonth(month, language) })}
    >
      {children}
    </Link>
  );
}

function ForecastCurrencyTable({
  table,
  filters,
  tenantMonth,
  language,
}: {
  readonly table: ForecastTable;
  readonly filters: Pick<SalesForecastSearch, "owner" | "customer">;
  readonly tenantMonth: string | undefined;
  readonly language: string;
}) {
  const { t, i18n } = useTranslation();
  const lines = forecastLines(table);
  const money = (minor: number) => formatMinor(minor, table.currency, language);
  // The bar's scale: the largest weighted value in this table. Presentation
  // only; the number beside it is the served one.
  let peak = 0;
  for (const line of lines) if (line.weightedValue > peak) peak = line.weightedValue;
  const slippedList = tenantMonth === undefined ? undefined : addMonths(tenantMonth, -1);

  const link = (line: ForecastLine, content: ReactNode, testId: string) => {
    if (line.month !== undefined) {
      return (
        <CellLink month={line.month} filters={filters} testId={testId} language={language}>
          {content}
        </CellLink>
      );
    }
    if (slippedList === undefined) return <span className="num">{content}</span>;
    return (
      <Link
        to="/sales/opportunities"
        search={compactSearch({
          close_to: slippedList,
          owner: filters.owner,
          customer: filters.customer,
        })}
        className="ds-lnk num"
        data-testid={testId}
        data-month="slipped"
      >
        {content}
      </Link>
    );
  };

  const columns: DataTableColumn<ForecastLine>[] = [
    {
      key: "month",
      header: t("sales.pipeline.forecast.colMonth"),
      id: true,
      render: (line) =>
        line.month === undefined ? (
          <span
            data-testid="sales-forecast-slipped"
            title={t("sales.pipeline.forecast.slippedHint")}
          >
            {t("sales.pipeline.forecast.slipped")}
          </span>
        ) : (
          formatMonth(line.month, language)
        ),
    },
    {
      key: "deals",
      header: t("sales.pipeline.forecast.colDeals"),
      numeric: true,
      render: (line) => link(line, line.openCount, "sales-forecast-deals"),
    },
    {
      key: "pipeline",
      header: t("sales.pipeline.forecast.colPipeline"),
      numeric: true,
      render: (line) => link(line, money(line.openValue), "sales-forecast-pipeline"),
    },
    {
      key: "weighted",
      header: t("sales.pipeline.forecast.colWeighted"),
      numeric: true,
      render: (line) => (
        <span className="sales-forecast-weighted">
          {/* A fixed track, so the bar's percentage has a length to resolve
              against: the bar is as long as its share of the table's peak. */}
          <span className="sales-forecast-track" aria-hidden="true">
            <span
              className="sales-forecast-bar"
              data-testid="sales-forecast-bar"
              style={{
                width: `${peak > 0 ? Math.round((line.weightedValue / peak) * 100) : 0}%`,
              }}
            />
          </span>
          {link(line, money(line.weightedValue), "sales-forecast-weighted")}
        </span>
      ),
    },
    {
      key: "won",
      header: t("sales.pipeline.forecast.colWon"),
      numeric: true,
      render: (line) =>
        line.wonValue === undefined ? null : (
          <span className="sales-forecast-won">
            <span className="num" data-testid="sales-forecast-won" data-count={line.wonCount}>
              {money(line.wonValue)}
            </span>
            {line.wonUnvalued !== undefined && line.wonUnvalued > 0 ? (
              <span
                className="sales-hint num"
                data-testid="sales-forecast-won-unvalued"
                data-count={line.wonUnvalued}
              >
                {t("sales.pipeline.forecast.wonUnvalued", { count: line.wonUnvalued })}
              </span>
            ) : null}
          </span>
        ),
    },
    {
      key: "unvalued",
      header: t("sales.pipeline.forecast.colUnvalued"),
      numeric: true,
      render: (line) => link(line, line.unvaluedCount, "sales-forecast-unvalued"),
    },
  ];

  return (
    <section
      className="sales-forecast-table"
      data-testid="sales-forecast-table"
      data-currency={table.currency}
    >
      <h2 className="sales-forecast-caption">
        {t("sales.pipeline.forecast.caption", { currency: table.currency })}
      </h2>
      <DataTable
        clientPager={bookClientPager(t, i18n.language)}
        columns={columns}
        rows={lines}
        rowKey={(line) => line.key}
        caption={t("sales.pipeline.forecast.caption", { currency: table.currency })}
        rowTestId="sales-forecast-row"
        rowProps={(line) => ({
          "data-month": line.month ?? "slipped",
          "data-weighted": line.weightedValue,
          "data-open": line.openValue,
        })}
        empty={null}
      />
      {table.wonCancelled > 0 ? (
        <p className="sales-hint" data-testid="sales-forecast-cancelled-wins">
          {t("sales.pipeline.forecast.cancelledWins", { count: table.wonCancelled })}
        </p>
      ) : null}
    </section>
  );
}
